/**
 * Jul-2026 (User-Wunsch) — Grünes Hinweisfeld für automatische Kunden-Kontakte
 * (E-Mail/WhatsApp/SMS) mit Zahnrad:
 *   - Vorschau des Textes & der Anhänge, die versendet werden
 *   - Button „Versand einmalig aussetzen" (bzw. wieder aktivieren)
 * Wiederverwendbar überall dort, wo Kunden automatisch kontaktiert werden.
 */
import { useState } from "react";
import { Settings, FileText, ExternalLink, BellOff, Bell, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { useGearsVisible } from "../../lib/gearVisibility";

export function AutoContactHint({ children, previewUrl, skipActive, onToggleSkip, blockedInfo, testid = "auto-contact-hint" }) {
    // Aug-2026 (User-Wunsch): Zahnrad nur für Admin + freigegebene Accounts.
    const gearsOk = useGearsVisible();
    const [open, setOpen] = useState(false);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);

    const toggle = async () => {
        const next = !open;
        setOpen(next);
        if (next && !preview && previewUrl) {
            setLoading(true);
            try {
                const r = await api.get(previewUrl);
                setPreview(r.data);
            } catch { setPreview({ error: true }); }
            setLoading(false);
        }
    };

    if (blockedInfo) {
        return (
            <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded-sm text-xs text-red-800 flex gap-2" data-testid={`${testid}-blocked`}>
                <BellOff className="w-4 h-4 shrink-0" />
                <div><strong>Kein automatischer Versand:</strong> {blockedInfo}</div>
            </div>
        );
    }

    return (
        <div className={`border-l-4 rounded-sm text-xs ${skipActive ? "bg-gray-100 border-gray-400 text-gray-600" : "bg-emerald-50 border-emerald-500 text-emerald-900"}`} data-testid={testid}>
            <div className="p-3 flex gap-2 items-start">
                <FileText className="w-4 h-4 shrink-0" />
                <div className="flex-1">
                    {skipActive
                        ? <span><strong>Versand einmalig ausgesetzt</strong> — der Kunde wird bei diesem Vorgang NICHT automatisch kontaktiert.</span>
                        : children}
                </div>
                <button type="button" onClick={toggle} className={`shrink-0 text-gray-500 hover:text-gray-900 ${gearsOk ? "" : "hidden"}`} title="Versand-Text & Anhänge ansehen / Versand aussetzen" data-testid={`${testid}-gear`}>
                    <Settings className="w-4 h-4" />
                </button>
            </div>
            {open && (
                <div className="border-t border-emerald-200 bg-white text-gray-800 p-3 space-y-2" data-testid={`${testid}-panel`}>
                    {loading ? (
                        <div className="flex items-center gap-2 text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /> Lade Vorschau…</div>
                    ) : preview && !preview.error ? (
                        <>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                                Kanäle: {(preview.channels || []).join(" · ")}
                            </div>
                            {preview.email_subject && (
                                <div className="text-[11px]"><span className="font-bold">Betreff:</span> {preview.email_subject}</div>
                            )}
                            {preview.email_text && (
                                <pre className="text-[11px] whitespace-pre-wrap bg-gray-50 border border-gray-200 rounded-sm p-2 max-h-40 overflow-auto" data-testid={`${testid}-email-text`}>{preview.email_text}</pre>
                            )}
                            {preview.sms_text && (
                                <div className="text-[11px] bg-gray-50 border border-gray-200 rounded-sm p-2" data-testid={`${testid}-sms-text`}>
                                    <span className="font-bold">SMS/WhatsApp:</span> {preview.sms_text}
                                </div>
                            )}
                            {(preview.attachments || []).length > 0 && (
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Anhänge:</span>
                                    {preview.attachments.map((a) => (
                                        <a key={a.label} href={a.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] font-bold text-sky-700 hover:underline" data-testid={`${testid}-attachment-${a.label}`}>
                                            <ExternalLink className="w-3 h-3" /> {a.label}
                                        </a>
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-[11px] text-gray-400">Vorschau nicht verfügbar.</div>
                    )}
                    <button type="button" onClick={() => { onToggleSkip(!skipActive); setOpen(false); }}
                        className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-sm border ${skipActive ? "border-emerald-400 bg-emerald-50 text-emerald-800 hover:bg-emerald-100" : "border-red-300 bg-red-50 text-red-700 hover:bg-red-100"}`}
                        data-testid={`${testid}-skip-toggle`}>
                        {skipActive ? <><Bell className="w-3.5 h-3.5" /> Versand wieder aktivieren</> : <><BellOff className="w-3.5 h-3.5" /> Versand einmalig aussetzen</>}
                    </button>
                </div>
            )}
        </div>
    );
}
